import React, { useState } from "react";
import { CheckIcon, PencilIcon } from "@phosphor-icons/react";
import { Switch, Dialog, DialogActions, DialogContent, DialogTitle, Button } from "@mui/material";
import { CustomSearchInput, CustomOption, CustomTag } from "../../ui";
import "../../assets/css/general-view.css";

const colorPalette = {
    gris: "#C6C6C6",
    rouge: "#FFCFC7",
    orange: "#FFD0BB",
    jaune: "#F1CB9C",
    vert: "#C0E6C5",
    bleu: "#CDE6F9",
    violet: "#D6BAFC",
    rose: "#F8A9C5",
};

const GeneralView = ({ user, roles, onUpdateUser, onColorChange }) => {
    const [isEditingFirstName, setIsEditingFirstName] = useState(false);
    const [isEditingLastName, setIsEditingLastName] = useState(false);
    const [isEditingRole, setIsEditingRole] = useState(false);
    const [isEditingHoliday, setIsEditingHoliday] = useState(false);
    const [isEditingColor, setIsEditingColor] = useState(false);
    const [editedFirstName, setEditedFirstName] = useState(user.firstName);
    const [editedLastName, setEditedLastName] = useState(user.lastName);
    const [editedRoleId, setEditedRoleId] = useState(user.roleId);
    const [editedHolidayActive, setEditedHolidayActive] = useState(user.holidayActive);
    const [editedHolidayStartingBalance, setEditedHolidayStartingBalance] = useState(user.holidayStartingBalance);
    const [editedIsActive, setEditedIsActive] = useState(user.isActive);
    const [isEditingActive, setIsEditingActive] = useState(false);
    const [selectedColor, setSelectedColor] = useState(user.backgroundColor);
    const [isSaving, setIsSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleColorSelect = (color) => {
        setSelectedColor(color);
        setIsEditingColor(true);
        onColorChange(color);
    };

    const handleSave = async () => {
        setIsSaving(true);
        await onUpdateUser({
            ...user,
            firstName: editedFirstName,
            lastName: editedLastName,
            roleId: editedRoleId,
            holidayActive: editedHolidayActive,
            holidayStartingBalance: editedHolidayStartingBalance,
            backgroundColor: selectedColor,
            isActive: editedIsActive,
        });
        setIsSaving(false);
        resetEditing();
    };

    const resetEditing = () => {
        setIsEditingFirstName(false);
        setIsEditingLastName(false);
        setIsEditingRole(false);
        setIsEditingHoliday(false);
        setIsEditingColor(false);
        setIsEditingActive(false);
    };

    return (
        <div className="tab-panel">
            <div className="tab-panel-header">
                <h2>Dashboard</h2>
                <span>Vue générale de votre profil, email, rôle...</span>
            </div>

            <hr />

            <div className="dashboard-grid">
                <div className="profile-section">
                    <h3>Informations personnelles</h3>
                    
                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Prénom</span>
                            {isEditingFirstName ? (
                                <input 
                                    value={editedFirstName} 
                                    className="field-input" 
                                    onChange={(e) => setEditedFirstName(e.target.value)} 
                                    placeholder="Entrez un prénom" 
                                />
                            ) : (
                                <span className="field-value">{user.firstName}</span>
                            )}
                        </div>
                        <PencilIcon
                            size={20} 
                            className="edit-icon" 
                            onClick={() => setIsEditingFirstName(!isEditingFirstName)} 
                        />
                    </div>

                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Nom</span>
                            {isEditingLastName ? (
                                <input 
                                    value={editedLastName} 
                                    className="field-input" 
                                    onChange={(e) => setEditedLastName(e.target.value)} 
                                    placeholder="Entrez un nom" 
                                />
                            ) : (
                                <span className="field-value">{user.lastName}</span>
                            )}
                        </div>
                        <PencilIcon
                            size={20} 
                            className="edit-icon" 
                            onClick={() => setIsEditingLastName(!isEditingLastName)} 
                        />
                    </div>

                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Email</span>
                            <span className="field-value">{user.email}</span>
                        </div>
                    </div>
                </div>

                <div className="role-section">
                    <h3>Rôle & Permissions</h3>
                    
                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Rôle</span>
                            {isEditingRole ? (
                                <CustomSearchInput
                                    placeholder="Rechercher un rôle"
                                    entity={roles}
                                    value={roles.find((role) => role.id === editedRoleId)}
                                    onChange={(newValue) => setEditedRoleId(newValue.value)}
                                    Option={CustomOption}
                                />
                            ) : (
                                <CustomTag value={user.role.name} />
                            )}
                        </div>
                        <PencilIcon 
                            size={20} 
                            className="edit-icon" 
                            onClick={() => setIsEditingRole(!isEditingRole)} 
                        />
                    </div>
                </div>

                <div className="status-section">
                    <h3>Statut du compte</h3>

                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Compte actif</span>
                            {isEditingActive ? (
                                <Switch checked={editedIsActive} onChange={(e) => setEditedIsActive(e.target.checked)} />
                            ) : (
                                <CustomTag value={user.isActive ? "Oui" : "Non"} className={user.isActive ? "active" : "inactive"} />
                            )}
                        </div>
                        <PencilIcon
                            size={20}
                            className="edit-icon"
                            onClick={() => setIsEditingActive(!isEditingActive)}
                        />
                    </div>
                </div>

                <div className="holiday-section">
                    <h3>Gestion des congés</h3>

                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Congés activés</span>
                            {isEditingHoliday ? (
                                <div className="holiday-edit">
                                    <Switch checked={editedHolidayActive} onChange={(e) => setEditedHolidayActive(e.target.checked)} />
                                    {editedHolidayActive && (
                                        <input
                                            type="number"
                                            value={editedHolidayStartingBalance}
                                            min={0}
                                            step={0.01}
                                            className="field-input small"
                                            onChange={(e) => setEditedHolidayStartingBalance(e.target.value)}
                                            placeholder="Solde"
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="holiday-display">
                                    <CustomTag value={user.holidayActive ? "Oui" : "Non"} className={user.holidayActive ? "active" : "inactive"} />
                                    {user.holidayActive && (
                                        <span className="holiday-balance">
                                            {user.holidayStartingBalance} jours
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <PencilIcon
                            size={20} 
                            className="edit-icon" 
                            onClick={() => setIsEditingHoliday(!isEditingHoliday)} 
                        />
                    </div>
                </div>

                <div className="appearance-section">
                    <h3>Apparence</h3>
                    
                    <div className="profile-field">
                        <div className="field-info">
                            <span className="field-label">Couleur du profil</span>
                            <div className="color-display">
                                <div 
                                    className="color-preview" 
                                    style={{ backgroundColor: selectedColor }}
                                ></div>
                                <span className="color-value">{selectedColor}</span>
                            </div>
                        </div>
                        <PencilIcon
                            size={20} 
                            className="edit-icon" 
                            onClick={() => setDialogOpen(true)} 
                        />
                    </div>

                    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                        <DialogTitle>Choisissez une couleur</DialogTitle>
                        <DialogContent>
                            <div className="color-palette">
                                {Object.entries(colorPalette).map(([name, color]) => (
                                    <div
                                        key={name}
                                        className={`color-option ${selectedColor === color ? 'selected' : ''}`}
                                        onClick={() => {
                                            handleColorSelect(color);
                                            setDialogOpen(false);
                                        }}
                                        style={{ backgroundColor: color }}
                                        title={name}
                                    />
                                ))}
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
                        </DialogActions>
                    </Dialog>
                </div>
            </div>

            {(isEditingFirstName || isEditingLastName || isEditingRole || isEditingHoliday || isEditingColor || isEditingActive) && (
                <div className="save-section">
                    <button className="button-large blue-button" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? (
                            <div className="save-loader"></div>
                        ) : (
                            <>
                                <CheckIcon size={16} weight="bold" />
                                Enregistrer
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default GeneralView;
